import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
} from '@angular/core';
import { GlobalErrorHandler } from 'app/core/error/global-error.handler';
import { EK_DATE_FORMAT, EK_TIME_ZONE } from 'app/core/date/ek-date-format';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import {
    DateAdapter as CalendarDateAdapter,
    CalendarModule,
} from 'angular-calendar'; // Import CalendarModule and alias DateAdapter
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns'; // Or your preferred adapter
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideAdminAnalytics } from './core/analytics/admin-analytics.provider';
import { EK_FUSE_SCREENS } from './core/config/breakpoints';

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // GA4 (admin's own stream). Empty for builds that do not opt in — see
        // environment.*.ts `analytics.enabled` and core/analytics/.
        ...provideAdminAnalytics(),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },

        // Every date the app RENDERS is dd-MM-yyyy. Two separate mechanisms have to
        // agree on that, which is why it is stated twice here:
        //   - DATE_PIPE_DEFAULT_OPTIONS covers a bare `| date` in a template
        //   - MAT_DATE_FORMATS covers the text inside a datepicker input
        // Formats are Luxon tokens below (the adapter is LuxonDateAdapter) and
        // Angular tokens above; for dd-MM-yyyy they happen to be identical.
        // `timezone` alongside the format: without it a bare `| date` renders in
        // the BROWSER's zone, so an admin on a machine set to UTC read every
        // timestamp 5.5 hours off what the team in India sees. See EK_TIME_ZONE.
        {
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: { dateFormat: EK_DATE_FORMAT, timezone: EK_TIME_ZONE },
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    // Must accept what display.dateInput writes, or re-parsing
                    // the field's own text fails. The looser variants are here
                    // because people type 1-8-2026 and 18/08/2026.
                    dateInput: ['dd-MM-yyyy', 'd-M-yyyy', 'dd/MM/yyyy', 'd/M/yyyy'],
                },
                display: {
                    dateInput: 'dd-MM-yyyy',
                    monthYearLabel: 'LLL yyyy',
                    // Spoken, not shown -- a screen reader saying "eighteen dash
                    // oh eight dash..." is worse than "Aug 18, 2026".
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Angular Calendar Configuration
        importProvidersFrom(
            CalendarModule.forRoot({
                provide: CalendarDateAdapter, // Alias to avoid conflict with Material DateAdapter
                useFactory: adapterFactory,
            })
        ),

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () =>
                    new Promise<void>((resolve, reject) => {
                        translocoService.load(defaultLang).subscribe({
                            next: () => resolve(),
                            error: (err) => reject(err),
                            complete: () => resolve(),
                        });
                    });
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                // Shared with tailwind.config.js — see core/config/breakpoints.cjs
                screens: EK_FUSE_SCREENS,
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
